// src/pages/SelectFunction/index.js
import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
} from "reactstrap";

import BreadCrumb from "../../Components/Common/BreadCrumb";

// API
import { getPlusStatus } from "../../helpers/eirna_backend_helper";

// Import function icons
import subcodonIcon from "../../assets/images/eirna/functions/single_tran_icon.png";
import comparisonIcon from "../../assets/images/eirna/functions/comparison_icon.png";
import dataQualityIcon from "../../assets/images/eirna/functions/metainfo_icon.png";
import differentialIcon from "../../assets/images/eirna/functions/diff_icon.png";
import pausePredIcon from "../../assets/images/eirna/functions/pausepred_icon.png";
import genomeBrowserIcon from "../../assets/images/eirna/functions/single_genome_icon.png";
import comparisonGenomeBrowserIcon from "../../assets/images/eirna/functions/comparison_genome_icon.png";
import orfDetectionIcon from "../../assets/images/eirna/functions/orf_detection_icon.png";
import diffDistributionIcon from "../../assets/images/eirna/functions/diff_silhouette_icon.png";
import diffCDSIcon from "../../assets/images/eirna/functions/diff_uorf_icon.png";
import codonOccupancyIcon from "../../assets/images/eirna/functions/codon_occupancy_icon.png";
import transcriptomeInfoIcon from "../../assets/images/eirna/functions/traninfo_icon.png";
import timeSeriesIcon from "../../assets/images/eirna/functions/time_series_icon.png";
import readthroughIcon from "../../assets/images/eirna/functions/rrts_icon.png";
import polysomeIcon from "../../assets/images/eirna/functions/poly_heatmap_icon.png";
import batchIcon from "../../assets/images/eirna/functions/batch_icon.png";
import batchComparisonIcon from "../../assets/images/eirna/functions/batch_comparison_icon.png";
import rrtsIcon from "../../assets/images/eirna/functions/rrts_icon.png";
import utrPeriodicityIcon from "../../assets/images/eirna/functions/utr_periodicity_icon.png";
import terminationMetageneIcon from "../../assets/images/eirna/functions/termination_metagene_icon.png";
import sampleGroupingsIcon from "../../assets/images/eirna/functions/sample_groupings.png";


const SelectFunction = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [isBatchMode, setIsBatchMode] = useState(false);
  const [dropdownStates, setDropdownStates] = useState({});
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedFunction, setSelectedFunction] = useState(null);
  const [plusStatus, setPlusStatus] = useState(false);
  const [projectName, setProjectName] = useState("");

  useEffect(() => {
    const storedMappingString = localStorage.getItem("projectNamesMapping");
    
    if (storedMappingString) {
      try {
        const mapping = JSON.parse(storedMappingString);
        
        if (mapping[projectId]) {
          setProjectName(mapping[projectId].replaceAll("_", " "));
        }
      } catch (error) {
        console.error("Failed to parse project name mapping", error);
      }
    }
  }, [projectId]);


  // Check plus status
  useEffect(() => {
    const fetchStatus = async () => {
      const plus_status_response = await getPlusStatus(projectId);
      setPlusStatus(plus_status_response.plus_status)
    }

    if (projectId) {
      fetchStatus();
    }
  }, [
    projectId
  ])

  

  const handleFunctionClick = (func) => {
    // Check if function has subfunctions
    if (func.subfunctions && func.subfunctions.length > 0) {
      setSelectedFunction(func);
      setModalIsOpen(true);
    } else {
      navigate(func.route);
    }
  };

  const handleSubfunctionClick = (subfunction) => {
    setModalIsOpen(false);
    navigate(subfunction.route);
  };

  const toggleDropdown = (funcId) => {
    setDropdownStates((prev) => ({
      ...prev,
      [funcId]: !prev[funcId],
    }));
  };

  // Standard mode functions with subfunctions
  const singleFunctions = [
    {
      id: "subcodon",
      title: "Sub-codon position profile",
      description:
        `This plot can be used to view data aligned to specific transcripts, seq types
         like Ribo-seq and Disome-seq will be colored according to reading frame.`,
      icon: subcodonIcon,
      route: `/projects/${projectId}/interactive_plot/`,
      helpLink: "/help/plot/subcodon-profile",
    },
    {
      id: "comparison",
      title: "Comparison plot",
      description:
        "This plot can be used to compare data from two or more conditions on a single transcript.",
      icon: comparisonIcon,
      route: `/projects/${projectId}/comparison/`,
      helpLink: "/help/plot/comparison-plot",
    },
    {
      id: "data-quality",
      title: "Data Quality",
      description:
        "This page can be used to retrieve raw counts and produce various quality metric plots such as read length distribution, triplet periodicity and metagene plots among others.",
      icon: dataQualityIcon,
      route: `/projects/${projectId}/metainfo_plot/`,
      helpLink: "/help/plot/data-quality",
    },
    {
      id: "differential",
      title: "Differential Expression",
      description:
        "This page can be used to carry out differential expression/translation analysis using deltaTE.",
      icon: differentialIcon,
      route: `/projects/${projectId}/differential/`,
      helpLink: "/help/plot/differential-expression",
    },
    {
      id: "pause-prediction",
      title: "Pause Prediction",
      description:
        "This page can be used to detect pauses in ribosome profiling and disome-seq data, either in a single condition or directly comparing two groups.",
      icon: pausePredIcon,
      route: `/projects/${projectId}/pause_detection/`,
      helpLink: "/help/plot/pause-prediction",
    },
    {
      id: "genome-browser",
      title: "Genome browser",
      description:
        "This page can be used to view and explore genomic alignments.",
      icon: genomeBrowserIcon,
      route: `/projects/${projectId}/genome-browser-plots`,
      helpLink: null,
      subfunctions: [
        {
          id: "single-genome",
          title: "Single genome browser",
          description: "View individual samples on the genome browser",
          icon: genomeBrowserIcon,
          route: `/projects/${projectId}/single_genomebrowser/`,
        },
        {
          id: "comparison-genome",
          title: "Comparison genome browser",
          description: "Compare multiple samples on the genome browser",
          icon: comparisonGenomeBrowserIcon,
          route: `/projects/${projectId}/comparison_genomebrowser/`,
        },
      ],
    },
    {
      id: "translon-explorer",
      title: "Translon Explorer",
      description: "This page can be used to detect translons/translated ORFs.",
      icon: orfDetectionIcon,
      route: `/projects/${projectId}/translon_explorer/`,
      helpLink: "/help/plot/translon-explorer",
    },
    {
      id: "differential-distribution",
      title: "Differential distribution",
      description:
        'This links to a seperate page which lists plots related to differential distribution analysis plots ("Differential silhouette" and "Differential CDS/Leader").',
      icon: diffDistributionIcon,
      route: `/projects/${projectId}/differential-distribution`,
      helpLink: null,
      subfunctions: [
        {
          id: "differential-silhouette",
          title: "Differential silhouette",
          description:
            "Identify changes in transcript silhouettes between conditions",
          icon: diffDistributionIcon,
          route: `/projects/${projectId}/differential_silhouette/`,
        },
        {
          id: "differential-cds-leader",
          title: "Differential CDS/Leader",
          description: "Analyze changes in CDS/5'Leader expression ratios",
          icon: diffCDSIcon,
          route: `/projects/${projectId}/diff_leader_cds/`,
        },
      ],
    },
    {
      id: "codon-occupancy",
      title: "Codon Occupancy",
      description:
        "This page can be used to count the number of reads per codon in a set of samples, or to compare two sets of samples to see the differences in the number of reads per codon.",
      icon: codonOccupancyIcon,
      route: `/projects/${projectId}/codon_occupancy/`,
      helpLink: "/help/plot/codon-occupancy",
    },
    {
      id: "transcriptome-info",
      title: "Transcriptome Info",
      description:
        "This page can be used to get information from the transcriptome not linked to any specific dataset, for example lengths/GC content of a group of transcripts.",
      icon: transcriptomeInfoIcon,
      route: `/projects/${projectId}/traninfo_plot/`,
      helpLink: "/help/plot/transcriptome-info",
    },
    {
      id: "time-series",
      title: "Series Data (time series/dose response, etc.)",
      description:
        "This page can be used to carry out differential expression/translation analysis using DESeq2 across a customisable number of groups.",
      icon: timeSeriesIcon,
      route: `/projects/${projectId}/time_series/`,
      helpLink: "/help/plot/time-series",
    },
    {
      id: "readthrough",
      title: "Readthrough analysis",
      description:
        "This links to a seperate page which lists plots related to readthrough analysis.",
      icon: readthroughIcon,
      route: `/projects/${projectId}/readthrough-analysis`,
      helpLink: null,
      subfunctions: [
        {
          id: "rrts",
          title: "RRTS",
          description:
            "Ribosome Read Through Score (RRTS) analysis based on Wangen et al.",
          icon: rrtsIcon,
          route: `/projects/${projectId}/rrts/`,
        },
        {
          id: "utr-periodicity",
          title: "UTR Periodicity",
          description:
            "Grouped bar plots showing percentage of reads in different frames in CDS and 3' UTR",
          icon: utrPeriodicityIcon,
          route: `/projects/${projectId}/utr_periodicity/`,
        },
        {
          id: "termination-metagene",
          title: "Termination metagene",
          description:
            "Metagene profiles around CDS stop codons broken down by reading frame",
          icon: terminationMetageneIcon,
          route: `/projects/${projectId}/termination_metagene/`,
        },
      ],
    },
    {
      id: "polysome",
      title: "Polysome-seq analysis",
      description:
        "Polysome-seq analysis allows you to compare changes in polysome fractions between two conditions.",
      icon: polysomeIcon,
      route: `/projects/${projectId}/poly_shift/`,
      helpLink: "/help/plot/polysome-seq",
    },
  ];

  // Batch mode functions
  const batchFunctions = [
    {
      id: "batch-subcodon",
      title: "Batch sub-codon profiles",
      description:
        "This page can be used to generate multiple sub-codon profiles at once, results can be in PNG or HTML format, counts can also be generated in csv format. Results will be returned in a zipped folder.",
      icon: batchIcon,
      route: `/projects/${projectId}/batch_profiles/`,
      helpLink: "/help/plot/batch-subcodon",
      plusReq: true,
    },
    {
      id: "batch-comparison",
      title: "Batch comparison profiles",
      description:
        "This page can be used to generate multiple comparison profiles at once, results can be in PNG or HTML format, counts can also be generated in csv format. Results will be returned in a zipped folder.",
      icon: batchComparisonIcon,
      route: `/projects/${projectId}/batch_comparison_profiles/`,
      helpLink: "/help/plot/batch-comparison",
      plusReq: true,
    },
    {
      id: "batch-differential",
      title: "Batch Differential Expression",
      description:
        "This page can be used to carry out multiple DE comparisons as a single job.",
      icon: differentialIcon,
      route: `/projects/${projectId}/batch_differential/`,
      helpLink: "/help/plot/differential-expression",
      plusReq: true,
    },
    {
      id: "batch-codon-occupancy",
      title: "Batch Codon Occupancy",
      description:
        "This page can be used to carry out multiple codon occupancy comparisons as a single job.",
      icon: codonOccupancyIcon,
      route: `/projects/${projectId}/batch_codon_occupancy/`,
      helpLink: "/help/plot/codon-occupancy",
      plusReq: true,
    },
    {
      id: "batch-pause-prediction",
      title: "Batch Pause Prediction",
      description:
        "This page can be used to carry out multiple pause prediction comparisons as a single job.",
      icon: pausePredIcon,
      route: `/projects/${projectId}/batch_pause_detection/`,
      helpLink: "/help/plot/pause-prediction",
      plusReq: true,
    },
    {
      id: "batch-differential-silhouette",
      title: "Batch Differential Silhouette",
      description:
        "This page can be used to carry out multiple differential silhouette comparisons as a single job.",
      icon: diffDistributionIcon,
      route: `/projects/${projectId}/batch_differential_silhouette/`,
      helpLink: "/help/plot/differential-silhouette",
      plusReq: true,
    },
    {
      id: "batch-differential-cds-leader",
      title: "Batch Differential CDS/Leader",
      description:
        "This page can be used to carry out multiple CDS/Leader comparisons as a single job.",
      icon: diffCDSIcon,
      route: `/projects/${projectId}/batch_diff_leader_cds/`,
      helpLink: "/help/plot/diff-leader-cds",
      plusReq: true,
    },
    {
      id: "sample-grouping",
      title: "Sample grouping",
      description:
        "This page allows you to group samples into different conditions",
      icon: sampleGroupingsIcon,
      route: `/projects/${projectId}/sample_groupings/`,
      helpLink: null,
      plusReq: true,
    },
  ];

  const currentFunctions = isBatchMode ? batchFunctions : singleFunctions;
  const isPlusRestricted = isBatchMode && !plusStatus;

  const renderFunctionCard = (func) => {
    const isPlusRestricted = func.plusReq && !plusStatus;

    return (
    <Col lg={3} md={6} sm={12} key={func.id} className="mb-4">
      <Card
        className="function-card h-100"
        style={{
          cursor: isPlusRestricted ? "not-allowed" : "pointer",
          transition: "all 0.3s ease",
          borderRadius: "6px",
          overflow: "hidden",
          background: isPlusRestricted ? "#f3f4f6" : "#ffffff",
          border: "1px solid #e5e7eb",
          boxShadow: isPlusRestricted
            ? "none"
            : "0 2px 8px rgba(0, 0, 0, 0.08)",
          position: "relative",
          textAlign: isPlusRestricted ? 'center': 'left',
        }}
        onClick={() => {
          if (!isPlusRestricted) {
            handleFunctionClick(func);
          }
        }}
        onMouseEnter={(e) => {
          if (isPlusRestricted) return;

          e.currentTarget.style.boxShadow = "0 8px 25px rgba(0, 0, 0, 0.15)";
          e.currentTarget.style.transform = "translateY(-4px)";
          e.currentTarget.style.borderColor = "#059669";
          e.currentTarget.style.borderWidth = "2px";
        }}
        onMouseLeave={(e) => {
          if (isPlusRestricted) return;

          e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.08)";
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.borderColor = "#e5e7eb";
          e.currentTarget.style.borderWidth = "1px";
        }}
      >
        {/* Subfunctions indicator */}
        {func.subfunctions && func.subfunctions.length > 0 && (
          <div
            className="position-absolute"
            style={{
              top: "8px",
              left: "8px",
              zIndex: 5,
            }}
          >
            <div
              style={{
                background: "rgba(5, 150, 105, 0.9)",
                color: "white",
                fontSize: "10px",
                fontWeight: "600",
                padding: "3px 6px",
                borderRadius: "4px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              {func.subfunctions.length} Options
            </div>
          </div>
        )}

        {/* Three dots menu */}
        {!isPlusRestricted && (
          <div
            className="position-absolute"
            style={{
              top: "12px",
              right: "12px",
              zIndex: 10,
            }}
          >
            <Dropdown
              isOpen={dropdownStates[func.id] || false}
              toggle={() => toggleDropdown(func.id)}
            >
              <DropdownToggle
                tag="button"
                className="btn btn-sm border-0"
                style={{
                  background: "rgba(255, 255, 255, 0.9)",
                  borderRadius: "6px",
                  padding: "6px 8px",
                  color: "#6b7280",
                  fontSize: "16px",
                  backdropFilter: "blur(4px)",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <i className="ri-more-fill"></i>
              </DropdownToggle>
              <DropdownMenu end>
                <DropdownItem onClick={() => handleFunctionClick(func)}>
                  <i className="ri-play-line me-2"></i>
                  {func.subfunctions ? "View Options" : "Open Function"}
                </DropdownItem>
                {func.helpLink && (
                  <DropdownItem
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(func.helpLink, "_blank");
                    }}
                  >
                    <i className="ri-question-line me-2"></i>
                    Help & Tutorial
                  </DropdownItem>
                )}
              </DropdownMenu>
            </Dropdown>
          </div>
        )}

        {isPlusRestricted && (
          <div
            className="position-absolute"
            style={{
              top: "12px",
              right: "12px",
              zIndex: 10,
              background: "#059669",
              color: "#ffffff",
              fontSize: "11px",
              fontWeight: "600",
              padding: "4px 8px",
              borderRadius: "4px",
            }}
          >
            Connect+
          </div>
        )}

        <CardBody className="p-2" title={func.description}>
          {/* Chart Preview Image */}
          <div
            style={{
              height: "210px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "12px",
              opacity: isPlusRestricted ? 0.65 : 1,
              filter: isPlusRestricted ? "grayscale(100%)" : "none",
            }}
          >
            <img
              src={func.icon}
              alt={func.title}
              style={{
                maxHeight: "110%",
                maxWidth: "110%",
                objectFit: "contain",
              }}
              onError={(e) => {
                e.target.style.display = "none";
                const fallback = document.createElement("div");
                fallback.style.cssText = `
                width: 100%;
                height: 160px;
                background-color: #f3f4f6;
                display: flex;
                align-items: center;
                justify-content: center;
                color: #9ca3af;
                font-size: 48px;
                border-radius: 4px;
              `;
                fallback.innerHTML = '<i class="ri-bar-chart-line"></i>';
                e.target.parentNode.appendChild(fallback);
              }}
            />
          </div>

          {/* Title */}
          <h5
            className="fw-bold mb-2"
            style={{
              fontSize: "18px",
              color: "#1f2937",
              lineHeight: "1.3",
              marginTop: isPlusRestricted ? '30px': '0',
              marginBottom: isPlusRestricted ? '0': '8px',
              marginLeft: "10px",
            }}
          >
            {func.title}
            {func.subfunctions && func.subfunctions.length > 0 && (
              <i
                className="ri-arrow-down-s-line ms-1"
                style={{ fontSize: "16px", color: "#059669" }}
              />
            )}
          </h5>

          {/* Description */}
          {!isPlusRestricted && (
            <p
              style={{
                fontSize: "12px",
                lineHeight: "1.4",
                marginBottom: "0",
                marginLeft: "10px",
                color: "#6b7280",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {func.description}
            </p>
          )}


        </CardBody>
      </Card>
    </Col>
    );
  };

  document.title = "Select Function | EIRNABio Connect";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            breadcrumbs={[
              { label: "Home", url: "/dashboard" },
              { label: "Select Function" },
            ]}
          />

          {/* Header Section */}
          <Row className="mb-4">
            <Col lg={12}>
              <div className="d-flex justify-content-center align-items-center">
                <h3>{projectName || ""}</h3> 
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <h4
                  style={{
                    fontSize: "24px",
                    fontWeight: "600",
                    color: "#1f2937",
                    marginBottom: "0",
                  }}
                >
                  Select Function
                </h4>

                {/* Single/Batch Mode Toggle */}
                <div className="btn-group" role="group">
                  <Button
                    color={!isBatchMode ? "success" : "light"}
                    onClick={() => setIsBatchMode(false)}
                    style={{
                      backgroundColor: !isBatchMode ? "#059669" : "#f8f9fa",
                      borderColor: !isBatchMode ? "#059669" : "#d1d5db",
                      color: !isBatchMode ? "white" : "#6b7280",
                      fontWeight: "500",
                      fontSize: "14px",
                      padding: "8px 16px",
                      borderRadius: "8px 0 0 8px",
                    }}
                  >
                    <i className="ri-user-line me-1"></i>
                    Standard
                  </Button>
                  <Button
                    color={isBatchMode ? "success" : "light"}
                    onClick={() => setIsBatchMode(true)}
                    style={{
                      backgroundColor: isBatchMode ? "#059669" : "#f8f9fa",
                      borderColor: isBatchMode ? "#059669" : "#d1d5db",
                      color: isBatchMode ? "white" : "#6b7280",
                      fontWeight: "500",
                      fontSize: "14px",
                      padding: "8px 16px",
                      borderRadius: "0 8px 8px 0",
                      marginLeft: "-1px",
                    }}
                  >
                    <i className="ri-stack-line me-1"></i>
                    Connect +
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          {/* Connect+ Banner */}
          {isPlusRestricted && (
            <Alert color="info" className="mb-4">
              Connect+ accelerates your analysis by executing all relevant pair-wise
              comparisons across all functions simultaneously, eliminating the need
              to run individual differential comparisons. To discuss access to
              Connect+ please contact info@eirnabio.com
            </Alert>
          )}

          {/* Functions Grid - 4 functions per row */}
          <Row className="gx-3 gy-0">{currentFunctions.map(renderFunctionCard)}</Row>

          <Modal
            isOpen={modalIsOpen}
            toggle={() => setModalIsOpen(false)}
            size="xl"
            centered
            style={{ maxWidth: "1000px" }}
          >
            <ModalHeader
              toggle={() => setModalIsOpen(false)}
              className="border-bottom-0 pb-2"
            >
              <div className="d-flex align-items-center">
                {selectedFunction?.icon && (
                  <img
                    src={selectedFunction.icon}
                    alt={selectedFunction.title}
                    style={{
                      width: "48px",
                      height: "48px",
                      marginRight: "16px",
                      objectFit: "contain",
                    }}
                  />
                )}
                <div>
                  <h4 className="mb-1 fw-bold">{selectedFunction?.title}</h4>
                  <p className="text-muted mb-0" style={{ fontSize: "12px" }}>
                    Choose a specific analysis option
                  </p>
                </div>
              </div>
            </ModalHeader>

            <ModalBody className="pt-3" style={{ minHeight: "300px" }}>
              <Row className="g-4 justify-content-center">
                {selectedFunction?.subfunctions?.map((subfunction) => (
                  <Col key={subfunction.id}>
                    <Card
                      className="h-100 text-center"
                      style={{
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        border: "2px solid #e5e7eb",
                        borderRadius: "4px",
                        minHeight: "240px",
                        maxWidth: "350px",
                        margin: "0 auto",
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSubfunctionClick(subfunction);
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = "#059669";
                        e.currentTarget.style.boxShadow =
                          "0 8px 24px rgba(5, 150, 105, 0.2)";
                        e.currentTarget.style.transform = "translateY(-2px)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "#e5e7eb";
                        e.currentTarget.style.boxShadow = "none";
                        e.currentTarget.style.transform = "translateY(0)";
                      }}
                    >
                      <CardBody className="p-4 d-flex flex-column align-items-center justify-content-between">
                        {/* Large image */}
                        <div
                          style={{
                            height: "160px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginBottom: "12px",
                            overflow: "hidden",
                          }}
                        >
                          <img
                            src={subfunction.icon}
                            alt={subfunction.title}
                            style={{
                              maxHeight: "100%",
                              maxWidth: "100%",
                              objectFit: "contain",
                              borderRadius: "4px",
                            }}
                            onError={(e) => {
                              e.target.style.display = "none";
                              const fallback = document.createElement("div");
                              fallback.style.cssText = `
                      width: 100%;
                      height: 160px;
                      background-color: #f3f4f6;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      color: #9ca3af;
                      font-size: 48px;
                      border-radius: 4px;
                    `;
                              fallback.innerHTML =
                                '<i class="ri-bar-chart-line"></i>';
                              e.target.parentNode.appendChild(fallback);
                            }}
                          />
                        </div>

                        {/* Title and description */}
                        <div className="text-center flex-grow-1 d-flex flex-column justify-content-center">
                          <h5
                            className="mb-2 fw-semibold"
                            style={{ fontSize: "17px" }}
                          >
                            {subfunction.title}
                          </h5>
                          <p
                            className="text-muted mb-0"
                            style={{
                              fontSize: "14px",
                              lineHeight: "1.4",
                              maxHeight: "60px",
                              overflow: "hidden",
                            }}
                          >
                            {subfunction.description}
                          </p>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>
            </ModalBody>

            <ModalFooter className="border-top-0 pt-2">
              <Button
                color="secondary"
                size="md"
                onClick={() => setModalIsOpen(false)}
              >
                <i className="ri-close-line me-1"></i> Cancel
              </Button>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default SelectFunction;
